import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LoadingScreen } from './components/LoadingScreen';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const MapWorldPage = lazy(() => import('./pages/MapWorldPage'));
const MotifGamePage = lazy(() => import('./pages/MotifGamePage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const CollectionPage = lazy(() => import('./pages/CollectionPage'));
const DistrictsPage = lazy(() => import('./pages/DistrictsPage'));
const StoriesPage = lazy(() => import('./pages/StoriesPage'));
const MadeInSalaPage = lazy(() => import('./pages/MadeInSalaPage'));
const ResponsibleTravelPage = lazy(() => import('./pages/ResponsibleTravelPage'));
const CityTodayPage = lazy(() => import('./pages/CityTodayPage'));
const CityProfilePage = lazy(() => import('./pages/CityProfilePage'));
const CulinaryPage = lazy(() => import('./pages/CulinaryPage'));
const ItinerariesPage = lazy(() => import('./pages/ItinerariesPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const View360Page = lazy(() => import('./pages/View360Page'));

function RouteTransitionOverlay() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    setIsVisible(true);
    const timer = window.setTimeout(() => setIsVisible(false), 900);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  const copy = useMemo(() => {
    const isEn = i18n.resolvedLanguage?.startsWith('en');
    const routeMap: Record<string, { eyebrow: string; title: string; hint: string }> = {
      '/map': isEn
        ? { eyebrow: 'Opening the city canvas', title: 'Solo 3D Map', hint: 'Preparing landmarks, districts, and spatial layers…' }
        : { eyebrow: 'Membuka kanvas kota', title: 'Peta Solo 3D', hint: 'Menyiapkan landmark, kawasan, dan lapisan ruang kota…' },
      '/360': isEn
        ? { eyebrow: 'Entering immersive mode', title: 'Solo 360°', hint: 'Bringing streets, courtyards, and viewpoints into view…' }
        : { eyebrow: 'Masuk ke mode imersif', title: 'Solo 360°', hint: 'Menghadirkan jalan, halaman, dan sudut pandang kota…' },
      '/made-in-sala': isEn
        ? { eyebrow: 'Meeting local makers', title: 'Made in Sala', hint: 'Preparing studios, crafts, and living traditions…' }
        : { eyebrow: 'Menuju karya warga', title: 'Made in Sala', hint: 'Menyiapkan studio, kriya, dan tradisi yang terus hidup…' },
      '/stories': isEn
        ? { eyebrow: 'Listening to local voices', title: 'Stories of Sala', hint: 'Gathering voices, memories, and human stories…' }
        : { eyebrow: 'Mendengarkan suara warga', title: 'Cerita Kota', hint: 'Merangkai suara, kenangan, dan kisah manusia…' },
      '/culinary': isEn
        ? { eyebrow: 'Following the flavor trail', title: 'Rasa Sala', hint: 'Setting the table with dishes, aromas, and food stories…' }
        : { eyebrow: 'Mengikuti jejak rasa', title: 'Rasa Sala', hint: 'Menyiapkan hidangan, aroma, dan cerita kuliner…' },
      '/itineraries': isEn
        ? { eyebrow: 'Preparing your route', title: 'Curated journeys', hint: 'Composing routes, timing, and memorable stops…' }
        : { eyebrow: 'Menyiapkan rute perjalanan', title: 'Perjalanan Pilihan', hint: 'Menyusun rute, waktu, dan perhentian yang berkesan…' },
      '/game/motif': isEn
        ? { eyebrow: 'Entering play mode', title: 'Batik motif game', hint: 'Loading patterns, challenges, and the leaderboard…' }
        : { eyebrow: 'Masuk ke mode bermain', title: 'Permainan Motif Batik', hint: 'Memuat pola, tantangan, dan papan peringkat…' },
    };

    return routeMap[location.pathname] ?? (isEn
      ? { eyebrow: 'Transitioning through the city', title: 'AmertaSala', hint: 'Preparing the next scene in your Solo journey…' }
      : { eyebrow: 'Berpindah menyusuri kota', title: 'AmertaSala', hint: 'Menyiapkan adegan berikutnya dalam perjalananmu di Solo…' });
  }, [i18n.resolvedLanguage, location.pathname]);

  return (
    <AnimatePresence>
      {isVisible ? (
        <motion.div
          className="route-transition"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden="true"
        >
          <motion.div
            className="route-transition__panel"
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -10 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="route-transition__pattern" aria-hidden="true" />
            <div className="route-transition__crest" aria-hidden="true">
              <span></span><span></span><span></span>
              <i>A</i>
            </div>
            <p>{copy.eyebrow}</p>
            <h2>{copy.title}</h2>
            <strong>{copy.hint}</strong>
            <div className="route-transition__progress" aria-hidden="true"><span /></div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/districts" element={<DistrictsPage />} />
        <Route path="/stories" element={<StoriesPage />} />
        <Route path="/made-in-sala" element={<MadeInSalaPage />} />
        <Route path="/responsible" element={<ResponsibleTravelPage />} />
        <Route path="/today" element={<CityTodayPage />} />
        <Route path="/profile" element={<CityProfilePage />} />
        <Route path="/culinary" element={<CulinaryPage />} />
        <Route path="/itineraries" element={<ItinerariesPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/map" element={<MapWorldPage />} />
        <Route path="/360" element={<View360Page />} />
        <Route path="/game/motif" element={<MotifGamePage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/collection" element={<CollectionPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <RouteTransitionOverlay />
    </Suspense>
  );
}
